import { createTheme, alpha } from '@mui/material/styles';

// Brand Colors
const brandColor = {
  primary: '#2563eb', // Blue 600
  secondary: '#3b82f6', // Blue 500
  success: '#10b981', // Emerald 500
  warning: '#f59e0b', // Amber 500
  error: '#ef4444', // Red 500
  info: '#0ea5e9', // Sky 500
};

// Backgrounds
const background = {
  default: '#f8fafc', // Slate 50
  paper: '#ffffff',
  subtle: '#f1f5f9', // Slate 100
};

// Text
const text = {
  primary: '#1e293b', // Slate 800
  secondary: '#64748b', // Slate 500
  disabled: '#94a3b8', // Slate 400
};

export const theme = createTheme({
  palette: {
    primary: {
      main: brandColor.primary,
      light: '#60a5fa',
      dark: '#1d4ed8',
      contrastText: '#ffffff',
    },
    secondary: {
      main: brandColor.secondary,
      light: '#93c5fd',
      dark: '#2563eb',
      contrastText: '#ffffff',
    },
    success: {
      main: brandColor.success,
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: brandColor.warning,
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: brandColor.error,
      light: '#f87171',
      dark: '#dc2626',
    },
    info: {
      main: brandColor.info,
      light: '#60a5fa',
      dark: '#2563eb',
    },
    background: {
      default: background.default,
      paper: background.paper,
    },
    text: {
      primary: text.primary,
      secondary: text.secondary,
      disabled: text.disabled,
    },
    divider: '#e2e8f0', // Slate 200
  },
  typography: {
    fontFamily: '"Inter", "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.025em',
      color: text.primary,
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.025em',
      color: text.primary,
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.025em',
      color: text.primary,
    },
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.025em',
      color: text.primary,
    },
    h5: {
      fontWeight: 600,
      color: text.primary,
    },
    h6: {
      fontWeight: 600,
      color: text.primary,
    },
    subtitle1: {
      fontWeight: 500,
      color: text.primary,
    },
    subtitle2: {
      fontWeight: 500,
      color: text.secondary,
    },
    body1: {
      lineHeight: 1.6,
      color: text.primary,
    },
    body2: {
      lineHeight: 1.5,
      color: text.secondary,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none', // No all-caps buttons
    },
  },
  shape: {
    borderRadius: 12, // More rounded
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: '#cbd5e1 #f1f5f9',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            backgroundColor: '#f1f5f9',
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: '#cbd5e1',
            minHeight: 24,
            border: '2px solid #f1f5f9',
          },
          '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
            backgroundColor: '#94a3b8',
          },
          '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
            backgroundColor: '#94a3b8',
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#94a3b8',
          },
          '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
            backgroundColor: '#f1f5f9',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: '#1d4ed8', // Blue 700
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)', // Soft shadow
          border: '1px solid #e2e8f0', // Slate 200
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: alpha('#ffffff', 0.8),
          backdropFilter: 'blur(12px)',
          color: text.primary,
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          borderBottom: '1px solid #e2e8f0',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          color: text.secondary,
          backgroundColor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
        },
        root: {
          borderBottom: '1px solid #f1f5f9',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 8,
        },
        filled: {
          border: '1px solid transparent',
        },
        outlined: {
          borderColor: '#e2e8f0',
        },
      },
    },
  },
});
